import { useContext, useMemo } from "react";
import {
  Modal,
  ModalContext,
  toast,
  ViewerContext,
  Spinner,
  Icon,
  useBackendErrorMessage,
} from "dash-ui";
import {
  useCancelProductSubscriptionMutation,
  GetCurrentAccountProductSubscriptionDocument,
  GetCurrentAccountTrialProductSubscriptionsDocument,
} from "gql/generated.graphql";
import { useTranslation } from "react-i18next";
import { getDisplayName } from "common/components/SubscriptionComponents/SubscriptionPlanCardV3/SubscriptionPlanCardV3.helpers";
import { getSubscriptionTheme } from "common/components/SubscriptionComponents/SubscriptionComponents.constants";
import { formatEndDate } from "pages/ProfilePage/components/ProfileBilling/components/SubscriptionChangeConfirmationModalV2/SubscriptionChangeConfirmationModalV2.helpers";
import { formatDateString } from "pages/ProfilePage/components/ProfileBilling/ProfileBilling.helpers";
import { AnalyticsEvent, trackEvent } from "shared/analytics";

interface CancelTrialModalProps {
  previousPlanName?: string;
}

const CancelTrialModal = ({ previousPlanName }: CancelTrialModalProps) => {
  const { isOpenCurrent, toggleModal } = useContext(ModalContext);
  const { subscription, setSubscription } = useContext(ViewerContext);
  const isOpen = isOpenCurrent["CancelTrialModal"];
  const [cancelProductSubscriptionMutation, { loading }] =
    useCancelProductSubscriptionMutation();
  const { t: billingPageLabels } = useTranslation("pages", {
    keyPrefix: "profilePage.billing",
  });

  const { t: tc } = useTranslation(["pages", "common"]);
  const translate = useBackendErrorMessage();
  const { t: revertLabels } = useTranslation("pages", {
    keyPrefix: "profilePage.billing.trialRevert",
  });

  const closeModal = () => toggleModal(false, "CancelTrialModal");

  const returnsToPreviousPlan = !!subscription?.metadata?.trialRollbackPriceId;

  const handleCancelTrialClick = () => {
    cancelProductSubscriptionMutation({
      variables: {
        input: {
          cancelationReason: "CANCELED_TRIAL",
        },
      },
      refetchQueries: [
        { query: GetCurrentAccountProductSubscriptionDocument },
        { query: GetCurrentAccountTrialProductSubscriptionsDocument },
      ],
      awaitRefetchQueries: true, // Wait for refetch to complete before onCompleted
      onCompleted: (data) => {
        // The schedule reaches ViewerContext with the webhook; until then the
        // card would still offer "Cancel trial".
        const canceledSubscription = data.cancelProductSubscription;
        if (returnsToPreviousPlan && canceledSubscription?.schedule) {
          setSubscription?.({ ...subscription, ...canceledSubscription });
        }
        trackEvent(AnalyticsEvent.TRIAL_CANCELED, {
          product_name: subscription?.subscriptionProduct?.name,
        });
        closeModal();
        toast.success(
          returnsToPreviousPlan
            ? revertLabels("cancelTrialToast", {
                previousPlan: previousPlanName,
                date: formatDateString(
                  canceledSubscription?.schedule?.nextPhaseStart ||
                    subscription?.currentPeriodEndAt
                ),
              })
            : billingPageLabels("subscriptionProductChanged")
        );
        localStorage.removeItem("notificationDismissTimestamp");
      },
      onError: (error) => {
        const errorMessage =
          error.message === "SUBSCRIPTION_ALREADY_CANCELED"
            ? tc("pages:profilePage.billing.cancelTrial.alreadyCanceled")
            : translate(
                error,
                tc("pages:profilePage.billing.cancelTrial.cancelError") || ""
              );
        toast.error(errorMessage);
      },
    });
  };

  const productTheme = useMemo(
    () =>
      subscription?.subscriptionProduct
        ? getSubscriptionTheme(subscription.subscriptionProduct.name)
        : getSubscriptionTheme("Basic"),
    [subscription?.subscriptionProduct?.name]
  );

  const displayName = useMemo(
    () =>
      subscription?.subscriptionProduct
        ? getDisplayName(subscription.subscriptionProduct.name)
        : "",
    [subscription?.subscriptionProduct?.name]
  );

  if (!isOpen) return null;

  const trialEndDate =
    subscription?.currentPeriodEndAt &&
    formatEndDate(subscription.currentPeriodEndAt);
  const rollbackEndDate =
    subscription?.currentPeriodEndAt &&
    formatDateString(subscription.currentPeriodEndAt);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      maxWidth="500px"
      w="500px"
      h="auto"
    >
      <div className="flex flex-col w-full">
        {/* Header */}
        <div
          className="mb-6 -mx-6 -mt-6 px-6 pt-7 pb-7 rounded-t-lg relative"
          style={{ background: "linear-gradient(to right, #fff5f5, #ffe0e0)" }}
        >
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 p-1.5 bg-white hover:bg-gray-100 transition-colors rounded"
            aria-label={tc("common:labels.close") || ""}
          >
            <Icon name="close" size={16} color="#6B7280" />
          </button>
          <h2
            className="text-xl font-bold text-slate-800 pr-8"
            style={{ color: "#991b1b" }}
          >
            {tc("pages:profilePage.billing.cancelTrial.title")}
          </h2>
          <p
            className="text-sm text-slate-500 mt-1"
            style={{ color: "#991b1b" }}
          >
            {returnsToPreviousPlan
              ? revertLabels("cancelTrialSubtitle", {
                  previousPlan: previousPlanName,
                })
              : tc("pages:profilePage.billing.cancelTrial.subtitle")}
          </p>
        </div>

        {/* Current Plan Card */}
        <div
          className="border-l-4 p-4 rounded-lg mb-6"
          style={{
            borderLeftColor: productTheme.bg,
            backgroundColor: "#F8FAFC",
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-base font-semibold text-slate-800 mb-1">
                {displayName}
              </h3>
              <p className="text-sm text-slate-500">
                {tc("pages:profilePage.billing.cancelTrial.currentTrialNote")}
              </p>
            </div>
          </div>
        </div>

        {/* Information Section */}
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon
              name="warningOutlined"
              size={24}
              color="#d97706"
              className="flex-shrink-0 mt-0.5"
            />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">
                {tc("pages:profilePage.billing.cancelTrial.whatHappens")}
              </h4>
              <ul className="text-sm text-amber-800 space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <span>
                    <strong>
                      {returnsToPreviousPlan
                        ? revertLabels("cancelTrialUntil", {
                            date: rollbackEndDate,
                          })
                        : tc(
                            "pages:profilePage.billing.cancelTrial.untilDate",
                            {
                              date: trialEndDate || "",
                            }
                          )}
                    </strong>{" "}
                    {returnsToPreviousPlan
                      ? revertLabels("cancelTrialUntilText", {
                          plan: displayName,
                        })
                      : tc(
                          "pages:profilePage.billing.cancelTrial.keepFeatures"
                        )}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <span>
                    <strong>
                      {returnsToPreviousPlan
                        ? revertLabels("cancelTrialAfter", {
                            date: rollbackEndDate,
                          })
                        : tc(
                            "pages:profilePage.billing.cancelTrial.afterDate",
                            {
                              date: trialEndDate || "",
                            }
                          )}
                    </strong>{" "}
                    {returnsToPreviousPlan
                      ? revertLabels("cancelTrialAfterText", {
                          previousPlan: previousPlanName,
                        })
                      : tc("pages:profilePage.billing.cancelTrial.subtitle")}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-600 mt-0.5">•</span>
                  <span>
                    {returnsToPreviousPlan
                      ? revertLabels("cancelTrialNoCharge", {
                          plan: displayName,
                        })
                      : tc("pages:profilePage.billing.cancelTrial.noCharge")}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={closeModal}
            className="flex-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors text-sm"
          >
            {tc("pages:profilePage.billing.cancelTrial.keepTrial")}
          </button>
          <button
            onClick={handleCancelTrialClick}
            disabled={loading}
            className="flex-1 px-6 py-2.5 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            style={{ backgroundColor: "#dc2626" }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Spinner size={16} color={"white"} />
                {tc("pages:profilePage.billing.cancelDowngrade.canceling")}
              </span>
            ) : (
              tc("pages:profilePage.billing.cancelTrial.confirmButton")
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CancelTrialModal;
